/**
 * Chat bubble for a single message in a thread.
 *
 * Inbound → left, neutral background. Outbound → right, accent background.
 * Pending bubbles include a spinner; failed bubbles a warning icon.
 */
import type { ThreadMessage } from "../types/api";
import type { PendingMessage } from "../lib/pending";
import { bubbleTime, formatTon } from "../lib/format";
import { AlertIcon, CheckIcon, ClockIcon, SpinnerIcon } from "./Icons";

interface ConfirmedProps {
  message: ThreadMessage;
}

export function ConfirmedBubble({ message }: ConfirmedProps): JSX.Element {
  const isOut = message.direction === "out";
  return (
    <Bubble
      side={isOut ? "right" : "left"}
      comment={message.comment}
      amountNano={message.amount_nano}
      timestamp={message.chain_ts}
      status={null}
    />
  );
}

interface PendingProps {
  message: PendingMessage;
}

export function PendingBubble({ message }: PendingProps): JSX.Element {
  return (
    <Bubble
      side="right"
      comment={message.comment}
      amountNano={message.amount_nano}
      timestamp={new Date(message.created_at).toISOString()}
      status={message.status}
    />
  );
}

interface BubbleProps {
  side: "left" | "right";
  comment: string;
  amountNano: string;
  timestamp: string;
  status: PendingMessage["status"] | null;
}

function Bubble({ side, comment, amountNano, timestamp, status }: BubbleProps): JSX.Element {
  const isRight = side === "right";
  const tone = isRight
    ? "bg-tg-button text-tg-button-text"
    : "bg-tg-secondary-bg text-tg-text";
  return (
    <div
      className={`flex w-full animate-slide-up ${isRight ? "justify-end" : "justify-start"}`}
    >
      <div
        className={`max-w-[78%] rounded-2xl px-3.5 py-2 shadow-sm ${tone} ${
          isRight ? "rounded-br-md" : "rounded-bl-md"
        }`}
      >
        <p className="whitespace-pre-wrap break-words text-[15px] leading-snug">
          {comment}
        </p>
        <div
          className={`mt-1 flex items-center justify-end gap-1.5 text-[11px] ${
            isRight ? "text-tg-button-text/80" : "text-tg-hint"
          }`}
        >
          <span>{formatTon(amountNano)}</span>
          <span>&middot;</span>
          <span>{bubbleTime(timestamp)}</span>
          {status !== null ? <StatusIcon status={status} /> : null}
        </div>
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: PendingMessage["status"] }): JSX.Element {
  switch (status) {
    case "signing":
    case "pending":
      return <SpinnerIcon size={12} aria-label="pending" />;
    case "confirmed":
      return <CheckIcon size={12} aria-label="confirmed" />;
    case "failed":
      return <AlertIcon size={12} aria-label="failed" />;
    default:
      return <ClockIcon size={12} />;
  }
}
